import { httpClient } from 'src/utils/httpClient';
import { useSuspenseQuery } from 'src/utils/useSupenseQuery';
import { Brand, CouponData } from 'src/models/Brand';
import { defaultColors } from 'src/theme/palette';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { CityData } from 'src/models/Menu';

interface IFindByState {
  slugId: string;
  type: 'takeaway' | 'delivery' | 'dinein';
  is_preorder: boolean;
  required_time?: string;
}

const CouponMapper = (data: any) => {
  return {
    ...data,
    active_coupons: data.active_coupons.map((c: any) => ({
      ...c,
      valid_since: moment(c.valid_since),
      valid_until: moment(c.valid_until),
    })),
    finish_coupons: data.finish_coupons.map((c: any) => ({
      ...c,
      valid_since: moment(c.valid_since),
      valid_until: moment(c.valid_until),
    })),
  } as CouponData;
}

export class BrandRepository {
  keys = {
    one: (slugId: string) => ['brand', slugId],
    byState: (slugId: string, type: string, is_preorder: boolean) => ['menus', { slugId, type, is_preorder }],
    couponsByBrand: (brandId: number) => ['coupons', { brandId }],
  };

  find = async (slugId: string) => {
    const { data } = await httpClient.get<Brand>(`app/brands/${slugId}`);
    return data;
  };

  findByState = async ({ slugId, type, is_preorder, required_time }: IFindByState) => {
    const { data } = await httpClient.get<CityData[]>(
      `app/brands/${slugId}/menus-by-state?type=${type}&is_preorder=${is_preorder ? 1 : 0}${required_time ? "&required_time="+ required_time : ''}`
    );
    return data;
  };

  getCoupons = async (brandId: number) => {
    const { data } = await httpClient.get<CouponData>(`app/brands/${brandId}/coupons`);
    return CouponMapper(data);
  };
}

const repo = new BrandRepository();

export const useBrandQuery = (slugId: string) =>
  useSuspenseQuery({ queryKey: repo.keys.one(slugId), queryFn: () => repo.find(slugId) });

export const useMenusByStateQuery = (param: IFindByState) =>
  useSuspenseQuery({
    queryKey: repo.keys.byState(param.slugId, param.type, param.is_preorder),
    queryFn: () => repo.findByState(param),
  });

export const brandRepo = {
  getBrand: (companySlug: string | undefined) => ({
    queryKey: ['brand', { companySlug }],
    queryFn: async () => {
      try {
        const { data } = await httpClient.get<Brand>(`app/brands/${companySlug}`);
        return data;
      } catch {
        return {
          primary_color: defaultColors.primary_color,
          primary_text_color: defaultColors.primary_text_color,
          logo_image: '',
        };
      }
    },
  }),
};

export const useCompanyBrandingQuery = (companySlug: string | undefined) => {
  return useQuery({ ...brandRepo.getBrand(companySlug), staleTime: Infinity });
};

export const useGetCouponsQuery = (brandId: number) =>
  useQuery({ queryKey: repo.keys.couponsByBrand(brandId), queryFn: () => repo.getCoupons(brandId) });
